import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";

/** Generic quick-action dialog for DP profile: incentive, ev-payment, asset, payment, note */
export function ActionModal({ kind, dp, open, onClose, onSaved }) {
  const { role } = useApp();
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const cfg = {
    incentive: { title: "Add Incentive", testId: "modal-incentive" },
    "ev-payment": { title: "Record EV Payment", testId: "modal-ev-payment" },
    asset: { title: "Issue Asset", testId: "modal-asset" },
    payment: { title: "Record Payment", testId: "modal-payment" },
    note: { title: "Add Note", testId: "modal-note" },
  }[kind] || { title: "Action" };

  const submit = async () => {
    setSaving(true);
    try {
      if (kind === "incentive") {
        await api.post("/incentives", { dp_id: dp.id, name: f.name || "Custom Incentive", type: f.type || "Custom", amount: Number(f.amount || 0), _user: role });
      } else if (kind === "ev-payment") {
        await api.post("/ev/payments", { dp_id: dp.id, amount: Number(f.amount || 0), method: f.method || "Cash", status: f.status || "Paid", remarks: f.remarks, _user: role });
      } else if (kind === "asset") {
        await api.post("/assets", { dp_id: dp.id, asset: f.asset || "Delivery Bag", quantity: Number(f.quantity || 1), condition: f.condition || "Good", return_required: f.return_required === "Yes", _user: role });
      } else if (kind === "note") {
        await api.put(`/dp/${dp.id}`, { remarks_note: f.note, _user: role, _reason: "Note added" });
      } else if (kind === "payment") {
        await api.post("/ev/payments", { dp_id: dp.id, amount: Number(f.amount || 0), method: f.method || "Bank Transfer", status: "Paid", remarks: "Payout: " + (f.remarks || ""), _user: role });
      }
      toast.success(`${cfg.title} saved`);
      onSaved?.();
      onClose();
      setF({});
    } catch (e) { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card" data-testid={cfg.testId}>
        <DialogHeader><DialogTitle className="font-head">{cfg.title}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {kind === "incentive" && <>
            <div><Label>Incentive Name</Label><Input value={f.name || ""} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Weekend Bonus" /></div>
            <div><Label>Type</Label>
              <Select value={f.type} onValueChange={(v) => set("type", v)}><SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>{["Daily", "Weekly", "Monthly", "Rain", "Peak-hour", "Attendance", "Target", "Special", "Festival", "Performance", "Custom"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Amount (₹)</Label><Input type="number" value={f.amount || ""} onChange={(e) => set("amount", e.target.value)} /></div>
          </>}
          {(kind === "ev-payment" || kind === "payment") && <>
            <div><Label>Amount (₹)</Label><Input type="number" value={f.amount || ""} onChange={(e) => set("amount", e.target.value)} /></div>
            <div><Label>Method</Label>
              <Select value={f.method} onValueChange={(v) => set("method", v)}><SelectTrigger><SelectValue placeholder="Cash" /></SelectTrigger>
                <SelectContent>{["Cash", "UPI", "Bank Transfer", "Wallet"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select></div>
            {kind === "ev-payment" && <div><Label>Status</Label>
              <Select value={f.status} onValueChange={(v) => set("status", v)}><SelectTrigger><SelectValue placeholder="Paid" /></SelectTrigger>
                <SelectContent>{["Paid", "Pending", "Partially Paid"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select></div>}
            <div><Label>Remarks</Label><Input value={f.remarks || ""} onChange={(e) => set("remarks", e.target.value)} /></div>
          </>}
          {kind === "asset" && <>
            <div><Label>Asset</Label>
              <Select value={f.asset} onValueChange={(v) => set("asset", v)}><SelectTrigger><SelectValue placeholder="Delivery Bag" /></SelectTrigger>
                <SelectContent>{["Delivery Bag", "T-Shirt", "Raincoat", "Gloves", "Mobile Holder", "Helmet"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Quantity</Label><Input type="number" value={f.quantity || 1} onChange={(e) => set("quantity", e.target.value)} /></div>
            <div><Label>Condition</Label>
              <Select value={f.condition} onValueChange={(v) => set("condition", v)}><SelectTrigger><SelectValue placeholder="Good" /></SelectTrigger>
                <SelectContent>{["Good", "Fair", "Poor"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Return Required</Label>
              <Select value={f.return_required} onValueChange={(v) => set("return_required", v)}><SelectTrigger><SelectValue placeholder="No" /></SelectTrigger>
                <SelectContent><SelectItem value="Yes">Yes</SelectItem><SelectItem value="No">No</SelectItem></SelectContent></Select></div>
          </>}
          {kind === "note" && <div><Label>Note</Label><Textarea rows={4} value={f.note || ""} onChange={(e) => set("note", e.target.value)} /></div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="action-modal-submit">{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
